/**
 * Typed fetch wrapper for the QuestGuardian API (spec §15).
 *
 * Auth model: HttpOnly session cookie (`qg_session`) + a readable CSRF cookie (`qg_csrf`).
 * The server signs the CSRF value with its own secret at login (QG-S001-01), so the SPA echoes
 * the cookie verbatim into `X-CSRF-Token` — no signing key is ever shipped in client assets.
 */
const BASE = "/api/v1";
const MUTATING = new Set(["POST", "PUT", "PATCH", "DELETE"]);

export interface ApiErrorBody {
  error: {
    code: string;
    message: string;
    request_id?: string | null;
    retryable?: boolean;
  };
}

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly retryable: boolean;
  readonly requestId?: string;
  constructor(status: number, body: ApiErrorBody) {
    super(body.error.message);
    this.code = body.error.code;
    this.status = status;
    this.retryable = body.error.retryable ?? false;
    this.requestId = body.error.request_id ?? undefined;
  }
}

export function getCookie(name: string): string | null {
  const entry = document.cookie.split("; ").find((c) => c.startsWith(`${name}=`));
  return entry ? decodeURIComponent(entry.slice(name.length + 1)) : null;
}

export type QueryParams = Record<string, string | number | boolean | null | undefined>;

export function withQuery(path: string, params?: QueryParams): string {
  if (!params) return path;
  const qs = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== null)
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
    .join("&");
  return qs ? `${path}${path.includes("?") ? "&" : "?"}${qs}` : path;
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = { "X-Request-ID": crypto.randomUUID() };
  if (MUTATING.has(method)) {
    headers["Content-Type"] = "application/json";
    const csrfCookie = getCookie("qg_csrf");
    if (csrfCookie) headers["X-CSRF-Token"] = csrfCookie;  // echo the server-signed value as-is
  }
  const res = await fetch(`${BASE}${path}`, {
    method,
    credentials: "include",
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!res.ok) {
    let parsed: ApiErrorBody;
    try {
      parsed = (await res.json()) as ApiErrorBody;
    } catch {
      parsed = { error: { code: "unknown_error", message: `${method} ${path} failed (${res.status})` } };
    }
    throw new ApiError(res.status, parsed);
  }
  const text = await res.text();
  return (text ? JSON.parse(text) : {}) as T;
}

export const api = {
  get: <T>(path: string, params?: QueryParams) => request<T>("GET", withQuery(path, params)),
  post: <T>(path: string, body?: unknown) => request<T>("POST", path, body),
  put: <T>(path: string, body?: unknown) => request<T>("PUT", path, body),
  del: <T>(path: string) => request<T>("DELETE", path),
};
